import React from 'react';
import { isQueryEmpty, reactQueryBuilder } from 'shared/utils/queryBuilder';
import { Card, Switch, Tag, Space } from 'antd';
import PreviewDates, { PeriodLabel } from 'components/DateRangePicker/date-preview';
import SortableList from 'components/SortableList';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { getFieldLabel, getFieldValueLabel } from 'components/QueryBuilder/QueryBuilderInput';
import useFormatMessage from 'hooks/useFormatMessage';

const { formatQuery, defaultRuleProcessorNL } = reactQueryBuilder;

const SEGMENTS_QUERY = gql`
  query Segments {
    segments {
      id
      title
      query
    }
  }
`;

export const getQuerySummary = ({ query, segments, $t, goalById, goalLoading = false }) => {
  if (isQueryEmpty(query)) {
    return '';
  }
  const ruleProcessor = (rule, options) => {
    if (rule.field === 'segment') {
      const segment = segments?.find((s) => s.id === rule.value);
      if (segment) {
        return getQuerySummary({ query: segment.query, segments, $t, goalById, goalLoading });
      }
      return `matches predefined segment ${rule.value}`;
    }

    const fieldLabel = getFieldLabel({ field: rule.field, value: rule.value, $t });

    if (rule.operator === 'matchesRegex') {
      const valueDisplay = rule.value != null ? String(rule.value) : '';
      return `${fieldLabel} matches regular expression ${valueDisplay}`;
    }

    if (rule.value === true || rule.value === false) {
      return fieldLabel;
    }

    rule = {
      ...rule,
      field: fieldLabel,
      value: rule.value.map?.(v => getFieldValueLabel(rule.field, v, $t, goalById, goalLoading)) || getFieldValueLabel(rule.field, rule.value, $t, goalById, goalLoading),
    };

    if (rule.operator === 'in' && rule.value?.length === 1) {
      rule.operator = '=';
    }
    return defaultRuleProcessorNL(rule, options);
  };

  return formatQuery(query, { format: 'natural_language', ruleProcessor });
};

const FilterSegment = ({ value, onChange, onClick, sortable, mainSegment }) => {
  const { data } = useQuery(SEGMENTS_QUERY);
  const $t = useFormatMessage();
  const isMain = value.id === mainSegment?.id;

  const isEmpty = (!value.dateRangeConfig && isQueryEmpty(value.query)) || !value.enabled;

  const onToggle = (enabled, e) => {
    e.stopPropagation();
    onChange({ ...value, enabled });
  };

  return (
    <Card
      className={`segment-card ${isEmpty ? 'empty' : ''} ${value.enabled ? 'enabled' : 'disabled'}`}
      style={value.enabled ? { '--ant-card-color': value.color } : undefined}
      hoverable={onClick}
      size="small"
      title={(
        <Space>
          {sortable && <SortableList.Handle />}
          {value?.title}
        </Space>
      )}
      extra={(
        <Space>
          {isMain && <Tag>Main segment</Tag>}
          {onChange && <Switch size="small" checked={value.enabled} onChange={onToggle} />}
        </Space>
      )}
      onClick={onClick ? () => onClick(value) : undefined}
    >
      <Space direction="vertical">
        {value.dateRangeConfig && <PeriodLabel dateRange={value.dateRangeConfig} />}
        {value.dateRangeConfig && <PreviewDates dateRange={value.dateRangeConfig} />}
        <div dangerouslySetInnerHTML={{ __html: getQuerySummary({ query: value.query, segments: data?.segments, $t }) }} />
      </Space>
    </Card>
  );
};

export default FilterSegment;
